/**
 * The single reporter for every failure React or the router observes.
 *
 * React 19's root callbacks fire once per failure, for every boundary in the
 * tree including react-router's own, and a callback supplied to createRoot
 * fully replaces React's default console + reportError path. That is what makes
 * one failure produce one report: the boundaries below render recovery UI and
 * report nothing, and logger.ts's window listeners are left for the errors React
 * never sees.
 */

import type { RootOptions } from 'react-dom/client'
import type { ClientOnErrorFunction } from 'react-router-dom'
import { logger, type LogError } from '@/utils/logger'

type CaughtErrorReporter = NonNullable<RootOptions['onCaughtError']>
type UncaughtErrorReporter = NonNullable<RootOptions['onUncaughtError']>
type RecoverableErrorReporter = NonNullable<RootOptions['onRecoverableError']>

const MODULE = 'rootErrorReporting'

/** JavaScript allows any value to be thrown; a report needs a name and a message. */
function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value))
}

/**
 * The failure is described here rather than passed to logger.error/critical's
 * positional `error` parameter, because that parameter overwrites stackTrace
 * with the JS stack — and the component stack is the one that names the
 * component that actually failed.
 */
function describe(
  error: Error,
  componentStack: string | null | undefined,
  recoveryAction: string
): LogError {
  return {
    type: error.name,
    message: error.message,
    stackTrace: componentStack ?? error.stack,
    recoveryAction,
  }
}

/** No boundary caught it: React has unmounted the whole root. */
export const reportUncaughtError: UncaughtErrorReporter = (error, errorInfo) => {
  logger.critical('React error escaped every boundary', undefined, {
    metadata: { module: MODULE, function: 'reportUncaughtError' },
    aiContext: {
      pattern: 'react_uncaught_error',
      tags: ['frontend', 'react', 'uncaught'],
      severity: 'critical',
      suggestedAction: 'Reload the page and investigate the failed component',
    },
    error: describe(
      toError(error),
      errorInfo.componentStack,
      'The root has unmounted — reload the page'
    ),
  })
}

/** A boundary caught it and is showing its fallback; the page survives. */
export const reportCaughtError: CaughtErrorReporter = (error, errorInfo) => {
  logger.error('React error caught by a boundary', undefined, {
    metadata: { module: MODULE, function: 'reportCaughtError' },
    aiContext: {
      pattern: 'react_caught_error',
      tags: ['frontend', 'react', 'caught'],
      severity: 'high',
      suggestedAction: 'Retry the failed section and investigate the component',
    },
    error: describe(
      toError(error),
      errorInfo.componentStack,
      'Retry the failed section or reload the page'
    ),
  })
}

/** React re-rendered successfully after the error; nothing is broken for the user. */
export const reportRecoverableError: RecoverableErrorReporter = (error, errorInfo) => {
  logger.warning('React recovered from an error', {
    metadata: { module: MODULE, function: 'reportRecoverableError' },
    error: describe(
      toError(error),
      errorInfo.componentStack,
      'None — React recovered on its own'
    ),
    aiContext: {
      pattern: 'react_recoverable_error',
      tags: ['frontend', 'react', 'recovered'],
      severity: 'low',
      suggestedAction: 'Investigate only if it repeats',
    },
  })
}

/**
 * Render errors reach react-router's own error boundary, so onCaughtError has
 * already reported them by the time this runs — only loader and action failures
 * are new here. Supplying this handler at all is what replaces react-router's
 * internal console.error.
 */
export const reportRouterError: ClientOnErrorFunction = (error, info) => {
  if (info.errorInfo) {
    return
  }

  logger.error('Router loader or action failed', undefined, {
    metadata: { module: MODULE, function: 'reportRouterError' },
    aiContext: {
      pattern: 'router_data_error',
      tags: ['frontend', 'router'],
      severity: 'high',
      suggestedAction: 'Investigate the failed route loader or action',
    },
    error: describe(
      toError(error),
      undefined,
      `Route ${info.pattern} is showing its error element`
    ),
  })
}
